/**
 * Vista: Detalle de Compra Final
 *
 * Muestra información completa de una compra:
 * - Desglose de costos
 * - Análisis de variaciones vs OC
 * - Listado de tractores
 * - Documentación
 */

import router from '../router/router.js';

export default {
  async render(params) {
    this.compraId = params.id;

    return `
      <div id="compraDetalleContainer">
        <div style="text-align: center; padding: 3rem;">
          ⏳ Cargando compra...
        </div>
      </div>
    `;
  },

  async afterRender() {
    // Exponer vista globalmente para onclick handlers
    window.compraDetalleView = this;
    await this.cargarCompra();
  },

  async cargarCompra() {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${window.API_BASE_URL}/compras/${this.compraId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Compra no encontrada');
        }
        throw new Error('Error al cargar la compra');
      }

      const result = await response.json();
      this.compra = result.data;

      this.renderCompra();
    } catch (error) {
      console.error('Error al cargar compra:', error);
      this.mostrarError(error.message);
    }
  },

  renderCompra() {
    const container = document.getElementById('compraDetalleContainer');
    const c = this.compra;

    container.innerHTML = `
      <!-- Header -->
      <div class="card">
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <h2 class="card-title">📦 ${c.folio}</h2>
            <p style="color: #666; margin: 0.5rem 0 0 0;">
              Registrada el ${this.formatDate(c.created_at)}
            </p>
          </div>
          <div style="display: flex; gap: 0.5rem;">
            <button class="btn btn-secondary" onclick="router.navigate('/compras')">
              ← Volver
            </button>
            <button class="btn btn-info" onclick="window.compraDetalleView.verAnalisis()">
              📊 Análisis
            </button>
          </div>
        </div>

        <!-- Información General -->
        <div class="card-body">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin-bottom: 2rem;">
            <div class="info-box">
              <div class="info-label">Proveedor</div>
              <div class="info-value">${c.proveedor_nombre}</div>
              <div class="info-hint">RFC: ${c.proveedor_rfc || 'N/A'}</div>
            </div>

            <div class="info-box">
              <div class="info-label">Orden de Compra</div>
              <div class="info-value">
                <span class="badge badge-info">${c.orden_compra_folio}</span>
              </div>
              <div class="info-hint">Fecha: ${this.formatDate(c.orden_compra_fecha)}</div>
            </div>

            <div class="info-box">
              <div class="info-label">Fecha de Compra</div>
              <div class="info-value">${this.formatDate(c.fecha_compra)}</div>
            </div>

            <div class="info-box">
              <div class="info-label">Fecha de Entrega</div>
              <div class="info-value">${this.formatDate(c.fecha_entrega)}</div>
            </div>
          </div>

          <!-- Desglose de Costos -->
          <h3 style="margin-bottom: 1rem; border-bottom: 2px solid #007bff; padding-bottom: 0.5rem;">
            💰 Desglose de Costos
          </h3>

          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 2rem;">
            <div class="stat-card" style="background: #f8f9fa;">
              <div class="stat-label">Costo Base</div>
              <div class="stat-value">$${this.formatNumber(c.costo_base)}</div>
              <div class="stat-hint">${c.moneda}</div>
            </div>

            <div class="stat-card" style="background: #e3f2fd;">
              <div class="stat-label">Accesorios</div>
              <div class="stat-value">$${this.formatNumber(c.costo_accesorios)}</div>
              <div class="stat-hint">${c.moneda}</div>
            </div>

            <div class="stat-card" style="background: #fff3e0;">
              <div class="stat-label">Impuestos</div>
              <div class="stat-value">$${this.formatNumber(c.costo_impuestos)}</div>
              <div class="stat-hint">${c.moneda}</div>
            </div>

            <div class="stat-card" style="background: #fce4ec;">
              <div class="stat-label">Gastos</div>
              <div class="stat-value">$${this.formatNumber(c.costo_gastos)}</div>
              <div class="stat-hint">${c.moneda}</div>
            </div>

            <div class="stat-card" style="background: #e8f5e9;">
              <div class="stat-label">Flete</div>
              <div class="stat-value">$${this.formatNumber(c.costo_flete)}</div>
              <div class="stat-hint">${c.moneda}</div>
            </div>

            <div class="stat-card" style="background: #007bff; color: white;">
              <div class="stat-label" style="color: white;">TOTAL</div>
              <div class="stat-value" style="color: white; font-size: 1.8rem;">
                $${this.formatNumber(c.total)}
              </div>
              <div class="stat-hint" style="color: white;">${c.moneda}</div>
            </div>
          </div>

          <!-- Análisis de Variaciones -->
          ${this.renderVariaciones(c)}

          <!-- Documentación -->
          <h3 style="margin: 2rem 0 1rem 0; border-bottom: 2px solid #007bff; padding-bottom: 0.5rem;">
            📄 Documentación
          </h3>

          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin-bottom: 2rem;">
            <div class="info-box">
              <div class="info-label">Número de Factura</div>
              <div class="info-value">${c.numero_factura}</div>
            </div>

            <div class="info-box">
              <div class="info-label">Pedimento</div>
              <div class="info-value">${c.pedimento || 'N/A'}</div>
            </div>

            <div class="info-box">
              <div class="info-label">Agente Aduanal</div>
              <div class="info-value">${c.agente_aduanal_nombre || 'N/A'}</div>
              ${c.agente_aduanal_rfc ? `<div class="info-hint">RFC: ${c.agente_aduanal_rfc}</div>` : ''}
            </div>

            ${c.tipo_cambio && c.tipo_cambio !== 1 ? `
              <div class="info-box">
                <div class="info-label">Tipo de Cambio</div>
                <div class="info-value">${c.tipo_cambio}</div>
                <div class="info-hint">Total en MXN: $${this.formatNumber(c.total * c.tipo_cambio)}</div>
              </div>
            ` : ''}
          </div>

          ${c.observaciones ? `
            <div class="info-box" style="margin-bottom: 2rem;">
              <div class="info-label">Observaciones</div>
              <div class="info-value" style="white-space: pre-wrap;">${c.observaciones}</div>
            </div>
          ` : ''}

          <!-- Tractores -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; border-bottom: 2px solid #007bff; padding-bottom: 0.5rem;">
            <h3 style="margin: 0;">
              🚜 Tractores Comprados (${c.detalles.length})
            </h3>
            ${this.renderBotonRegistrarTractores(c)}
          </div>

          <div style="display: grid; gap: 1rem;">
            ${c.detalles.map((detalle, index) => this.renderTractor(detalle, index + 1)).join('')}
          </div>
        </div>
      </div>
    `;
  },

  renderVariaciones(c) {
    const variacionMonto = c.variacion_monto || 0;
    const variacionPorcentaje = c.variacion_porcentaje || 0;
    const tipo = variacionMonto > 0 ? 'sobrecosto' : variacionMonto < 0 ? 'ahorro' : 'igual';
    const color = tipo === 'sobrecosto' ? '#dc3545' : tipo === 'ahorro' ? '#28a745' : '#6c757d';
    const bgColor = tipo === 'sobrecosto' ? '#fee' : tipo === 'ahorro' ? '#efe' : '#f8f9fa';

    return `
      <h3 style="margin: 2rem 0 1rem 0; border-bottom: 2px solid #007bff; padding-bottom: 0.5rem;">
        📊 Análisis de Variaciones vs OC
      </h3>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 2rem;">
        <div class="stat-card" style="background: #e3f2fd;">
          <div class="stat-label">Total OC</div>
          <div class="stat-value">$${this.formatNumber(c.orden_compra_total)}</div>
          <div class="stat-hint">${c.orden_compra_moneda || c.moneda}</div>
        </div>

        <div class="stat-card" style="background: #f3e5f5;">
          <div class="stat-label">Total Compra</div>
          <div class="stat-value">$${this.formatNumber(c.total)}</div>
          <div class="stat-hint">${c.moneda}</div>
        </div>

        <div class="stat-card" style="background: ${bgColor};">
          <div class="stat-label">Variación</div>
          <div class="stat-value" style="color: ${color};">
            ${variacionMonto >= 0 ? '+' : ''}$${this.formatNumber(Math.abs(variacionMonto))}
          </div>
          <div class="stat-hint" style="color: ${color}; font-weight: bold;">
            ${variacionMonto >= 0 ? '+' : ''}${variacionPorcentaje}%
          </div>
        </div>

        <div class="stat-card" style="background: ${bgColor}; border: 2px solid ${color};">
          <div class="stat-label">Tipo de Variación</div>
          <div class="stat-value" style="font-size: 1.2rem;">
            ${tipo === 'sobrecosto' ? '⚠️ Sobrecosto' : tipo === 'ahorro' ? '✅ Ahorro' : '➖ Sin variación'}
          </div>
        </div>
      </div>
    `;
  },

  renderBotonRegistrarTractores(c) {
    // DEPRECADO: el flujo viejo de "registrar en inventario desde compras" fue
    // reemplazado por el módulo Recepciones Visuales. Los tractores se crean al
    // asignar número de serie desde Recepciones → Por OC → Detalle de OC.
    // Se mantiene la función para no romper llamadas pero retorna cadena vacía.
    return '';
  },

  renderTractor(detalle, numero) {
    return `
      <div class="card" style="padding: 1rem; background: #f8f9fa;">
        <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 1rem;">
          <h4 style="margin: 0;">Tractor #${numero}</h4>
          <div style="display: flex; gap: 0.5rem; align-items: center;">
            ${this.renderTractorActions(detalle)}
          </div>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;">
          <div>
            <div class="info-label">Marca y Modelo</div>
            <div class="info-value">${detalle.marca} ${detalle.modelo}</div>
          </div>

          <div>
            <div class="info-label">Año</div>
            <div class="info-value">${detalle.anio}</div>
          </div>

          <div>
            <div class="info-label">Número de Serie</div>
            <div class="info-value">${detalle.numero_serie}</div>
            ${detalle.tractor_codigo ? `<div class="info-hint">Código: ${detalle.tractor_codigo}</div>` : ''}
          </div>

          ${detalle.numero_motor ? `
            <div>
              <div class="info-label">Número de Motor</div>
              <div class="info-value">${detalle.numero_motor}</div>
            </div>
          ` : ''}

          ${detalle.color ? `
            <div>
              <div class="info-label">Color</div>
              <div class="info-value">${detalle.color}</div>
            </div>
          ` : ''}

          ${detalle.hp ? `
            <div>
              <div class="info-label">Potencia</div>
              <div class="info-value">${detalle.hp} HP</div>
            </div>
          ` : ''}

          <div>
            <div class="info-label">Costo Unitario</div>
            <div class="info-value" style="color: #007bff; font-weight: bold;">
              $${this.formatNumber(detalle.costo_unitario)}
            </div>
          </div>

          ${detalle.tractor_status ? `
            <div>
              <div class="info-label">Estado en Inventario</div>
              <div class="info-value">
                <span class="badge badge-${this.getStatusColor(detalle.tractor_status)}">
                  ${detalle.tractor_status}
                </span>
              </div>
            </div>
          ` : ''}
        </div>

        ${detalle.observaciones ? `
          <div style="margin-top: 1rem; padding-top: 1rem; border-top: 1px solid #dee2e6;">
            <div class="info-label">Observaciones</div>
            <div class="info-value" style="white-space: pre-wrap;">${detalle.observaciones}</div>
          </div>
        ` : ''}
      </div>
    `;
  },

  getStatusColor(status) {
    const colors = {
      'disponible': 'success',
      'reservado': 'warning',
      'vendido': 'info',
      'mantenimiento': 'secondary'
    };
    return colors[status] || 'secondary';
  },

  renderTractorActions(detalle) {
    // Si no tiene numero_serie asignado
    if (!detalle.numero_serie) {
      return `
        <span class="badge badge-warning">⚠️ Sin número de serie</span>
      `;
    }

    // Si ya está registrado en inventario (tiene tractor_num_serie)
    if (detalle.tractor_num_serie) {
      return `
        <span class="badge badge-success">✅ En Inventario</span>
        <a
          href="/almacen/recepciones/nueva?num_serie=${detalle.numero_serie}"
          data-link
          class="btn btn-secondary btn-sm"
          title="Realizar inspección de calidad completa"
        >
          🔍 Inspección de Calidad
        </a>
      `;
    }

    // Pendiente de registro en inventario
    return `
      <span class="badge badge-info">📦 Listo para registrar</span>
    `;
  },

  // DEPRECADO: esta función invocaba POST /compras/:id/registrar-tractores que
  // ya no se usa. Con el rediseño, los tractores se crean desde el módulo
  // Recepciones Visuales al asignar el número de serie. Mantenida solo por
  // retrocompatibilidad en caso de llamadas viejas.
  async registrarTractoresEnInventario() {
    window.toast?.error?.(
      'Esta acción fue deprecada. Los tractores se crean desde Recepciones Visuales al asignar el número de serie.'
    );
  },

  async verAnalisis() {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${window.API_BASE_URL}/compras/${this.compraId}/analisis-variaciones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar análisis');

      const result = await response.json();
      this.mostrarModalAnalisis(result.data);
    } catch (error) {
      console.error('Error al cargar análisis:', error);
      this.mostrarAlerta('Error al cargar el análisis', 'error');
    }
  },

  mostrarModalAnalisis(analisis) {
    // Remover modal anterior si existe
    const existente = document.getElementById('modalAnalisisDetalle');
    if (existente) existente.remove();

    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.id = 'modalAnalisisDetalle';
    modal.innerHTML = `
      <div class="modal-content analisis-modal" style="max-width: 700px;">
        <div class="modal-header">
          <h3 class="modal-title">📊 Análisis Detallado de Variaciones</h3>
          <button class="modal-close" onclick="document.getElementById('modalAnalisisDetalle').remove()">✕</button>
        </div>
        <div class="modal-body">
          <div style="margin-bottom: 2rem;">
            <h4 style="margin-bottom: 0.5rem;">${analisis.compra_folio}</h4>
            <p style="color: #666;">Comparación con OC ${analisis.orden_compra_folio}</p>
          </div>

          <!-- Desglose de Costos -->
          <div style="margin-bottom: 2rem;">
            <h5 style="margin-bottom: 1rem;">Desglose de Costos</h5>
            <table class="table">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th style="text-align: right;">Monto</th>
                  <th style="text-align: right;">%</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Costo Base</td>
                  <td style="text-align: right;">$${this.formatNumber(analisis.costo_base)}</td>
                  <td style="text-align: right;">
                    ${((analisis.costo_base / analisis.compra_total) * 100).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>Accesorios</td>
                  <td style="text-align: right;">$${this.formatNumber(analisis.costo_accesorios)}</td>
                  <td style="text-align: right;">
                    ${((analisis.costo_accesorios / analisis.compra_total) * 100).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>Impuestos</td>
                  <td style="text-align: right;">$${this.formatNumber(analisis.costo_impuestos)}</td>
                  <td style="text-align: right;">
                    ${((analisis.costo_impuestos / analisis.compra_total) * 100).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>Gastos</td>
                  <td style="text-align: right;">$${this.formatNumber(analisis.costo_gastos)}</td>
                  <td style="text-align: right;">
                    ${((analisis.costo_gastos / analisis.compra_total) * 100).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>Flete</td>
                  <td style="text-align: right;">$${this.formatNumber(analisis.costo_flete)}</td>
                  <td style="text-align: right;">
                    ${((analisis.costo_flete / analisis.compra_total) * 100).toFixed(1)}%
                  </td>
                </tr>
                <tr style="font-weight: bold; background: #f8f9fa;">
                  <td>TOTAL COMPRA</td>
                  <td style="text-align: right;">$${this.formatNumber(analisis.compra_total)}</td>
                  <td style="text-align: right;">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Comparación con OC -->
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1rem; width: 100%; box-sizing: border-box;">
            <div class="stat-card" style="background: #e3f2fd;">
              <div class="stat-label">Total OC</div>
              <div class="stat-value">$${this.formatNumber(analisis.orden_compra_total)}</div>
            </div>

            <div class="stat-card" style="background: #f3e5f5;">
              <div class="stat-label">Total Compra</div>
              <div class="stat-value">$${this.formatNumber(analisis.compra_total)}</div>
            </div>

            <div class="stat-card" style="background: ${analisis.tipo_variacion === 'sobrecosto' ? '#fee' : '#efe'};">
              <div class="stat-label">Variación</div>
              <div class="stat-value" style="color: ${analisis.tipo_variacion === 'sobrecosto' ? '#dc3545' : '#28a745'};">
                ${analisis.variacion_monto >= 0 ? '+' : ''}$${this.formatNumber(analisis.variacion_monto)}
              </div>
              <div class="stat-hint" style="font-weight: bold; color: ${analisis.tipo_variacion === 'sobrecosto' ? '#dc3545' : '#28a745'};">
                ${analisis.variacion_porcentaje >= 0 ? '+' : ''}${analisis.variacion_porcentaje}%
              </div>
            </div>
          </div>

          <div class="info-box" style="background: ${analisis.tipo_variacion === 'sobrecosto' ? '#fee' : '#efe'}; border: 2px solid ${analisis.tipo_variacion === 'sobrecosto' ? '#dc3545' : '#28a745'}; width: 100%; box-sizing: border-box;">
            <div style="text-align: center;">
              <h4 style="margin: 0; color: ${analisis.tipo_variacion === 'sobrecosto' ? '#dc3545' : '#28a745'};">
                ${analisis.tipo_variacion === 'sobrecosto' ? '⚠️ SOBRECOSTO DETECTADO' : analisis.tipo_variacion === 'ahorro' ? '✅ AHORRO DETECTADO' : '➖ SIN VARIACIÓN'}
              </h4>
              ${analisis.tipo_variacion === 'sobrecosto' ? `
                <p style="margin: 0.5rem 0 0 0; color: #666;">
                  El costo real supera en ${analisis.variacion_porcentaje}% el costo base de la OC
                </p>
              ` : analisis.tipo_variacion === 'ahorro' ? `
                <p style="margin: 0.5rem 0 0 0; color: #666;">
                  El costo real es ${Math.abs(analisis.variacion_porcentaje)}% menor que la OC
                </p>
              ` : ''}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="document.getElementById('modalAnalisisDetalle').remove()">
            Cerrar
          </button>
        </div>
      </div>
    `;

    // Cerrar modal al hacer clic fuera
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    document.body.appendChild(modal);
  },

  mostrarError(mensaje) {
    const container = document.getElementById('compraDetalleContainer');
    container.innerHTML = `
      <div class="card">
        <div class="card-body" style="text-align: center; padding: 3rem;">
          <div style="font-size: 3rem; margin-bottom: 1rem;">⚠️</div>
          <h3 style="color: #dc3545; margin-bottom: 1rem;">${mensaje}</h3>
          <button class="btn btn-secondary" onclick="router.navigate('/compras')">
            ← Volver a Compras
          </button>
        </div>
      </div>
    `;
  },

  mostrarAlerta(mensaje, tipo = 'info') {
    const alertDiv = document.createElement('div');
    alertDiv.className = `alert alert-${tipo}`;
    alertDiv.textContent = mensaje;
    alertDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      padding: 1rem 1.5rem;
      border-radius: 4px;
      background: ${tipo === 'success' ? '#28a745' : tipo === 'error' ? '#dc3545' : '#007bff'};
      color: white;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    `;

    document.body.appendChild(alertDiv);

    setTimeout(() => {
      alertDiv.remove();
    }, 3000);
  },

  formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  formatNumber(num) {
    if (!num) return '0.00';
    return parseFloat(num).toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
};
